/**
 * Private file — the model (design pass 4, Part A; built 2026-10-04).
 *
 * WHAT IT IS. One running file per student of things the TEACHERS need to know
 * and the family must never see: a hoarse week, a family logistics remark, a
 * plan for whoever teaches her next. The rotation is why it exists — next week's
 * lesson is usually someone else's, so a private line has to survive the hand-off.
 * It replaces the Recap's "Teachers only" tier (P5), whose history migrates in as
 * one untagged entry per lesson (`origin: 'recap'`).
 *
 * STORE. `privateNotes/{studentId}/entries/{entryId}`:
 *   { text, authorId, authorName, lessonId | null, createdAt, tags[], mentions[] }
 *   + `origin: 'recap'` and `recapDocId` on migrated entries (Admin SDK only)
 *   + `editedAt` / `editedBy` after an admin edit.
 * A visibility boundary is a document boundary (core.md), so this is its own
 * collection: no family read rule reaches it, and none ever should.
 *
 * WHO. Any teacher reads every entry and adds; only an admin / owner edits or
 * deletes (Anthony, the same rule as Song Notes). firestore.rules enforces it;
 * the UI only mirrors it.
 *
 * TAGS are a FIXED list of eight topics (Anthony, 2026-10-02). There is
 * deliberately no wellbeing / safety tag. The keys are stored; labels and icons
 * live only here, and firestore.rules carries the same eight keys.
 * MENTIONS are staff Auth uids; a mention notifies (the bell + a push), it never
 * grants access — every teacher already reads every entry.
 */

export const PRIVATE_NOTE_TAGS = Object.freeze([
	{ key: 'vocal_health', label: 'Vocal health', icon: 'fa-heart-pulse' },
	{ key: 'technique', label: 'Technique focus', icon: 'fa-bullseye' },
	{ key: 'range', label: 'Range', icon: 'fa-arrows-up-down' },
	{ key: 'song_choice', label: 'Song', icon: 'fa-music' },
	{ key: 'practice', label: 'Practice', icon: 'fa-calendar-check' },
	{ key: 'confidence', label: 'Confidence', icon: 'fa-seedling' },
	{ key: 'performance', label: 'Performance prep', icon: 'fa-star' },
	{ key: 'family', label: 'Family / logistics', icon: 'fa-house-user' },
]);

export const PRIVATE_NOTE_TAG_KEYS = PRIVATE_NOTE_TAGS.map((t) => t.key);

export const MAX_PRIVATE_NOTE_LENGTH = 5000;

export const tagByKey = (key) => PRIVATE_NOTE_TAGS.find((t) => t.key === key) || null;

/** Unknown keys are dropped rather than shown as raw strings. */
export const knownTags = (tags) => (Array.isArray(tags) ? tags.filter((k) => tagByKey(k)) : []);

const toMillis = (v) => {
	if (!v) return 0;
	if (typeof v.toMillis === 'function') return v.toMillis();
	if (v instanceof Date) return v.getTime();
	if (typeof v === 'number') return v;
	return 0;
};

/**
 * Newest first. A just-saved entry has a null `createdAt` until the server
 * stamps it (the listener fires on the local write first), so null sorts as
 * NOW rather than as 1970, or the teacher's own entry would jump to the bottom.
 */
export const sortEntries = (entries, nowMs = Date.now()) =>
	[...(entries || [])].sort((a, b) => (toMillis(b.createdAt) || nowMs) - (toMillis(a.createdAt) || nowMs));

export const filterEntries = (entries, tagKey) =>
	tagKey ? (entries || []).filter((e) => Array.isArray(e.tags) && e.tags.includes(tagKey)) : entries || [];

export const tagCounts = (entries) => {
	const counts = Object.fromEntries(PRIVATE_NOTE_TAG_KEYS.map((k) => [k, 0]));
	(entries || []).forEach((e) => knownTags(e.tags).forEach((k) => (counts[k] += 1)));
	return counts;
};

/** "1 note" / "14 notes". */
export const countLabel = (n) => `${n} ${n === 1 ? 'note' : 'notes'}`;

/** The filter's count line: "3 Vocal health notes, newest first". */
export const filterLine = (n, tagKey) => {
	const tag = tagByKey(tagKey);
	const noun = n === 1 ? 'note' : 'notes';
	return tag ? `${n} ${tag.label} ${noun}, newest first` : `${n} ${noun}, newest first`;
};

/**
 * The when-line under an author: "Today 4:41 · this lesson", "Fri Sep 25 · her
 * lesson" (pronoun-free: "that lesson"), or "between lessons" for an entry written
 * from the profile. Pronouns are never guessed (core.md), so a past lesson reads
 * "that lesson" rather than "her lesson".
 */
export const whenLine = (entry, { currentLessonId = null, now = new Date() } = {}) => {
	const ms = toMillis(entry.createdAt) || now.getTime();
	const d = new Date(ms);
	const sameDay = d.toDateString() === now.toDateString();
	const date = sameDay
		? `Today ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/\s?[AP]M$/i, '')}`
		: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '');
	let where;
	if (!entry.lessonId) where = 'between lessons';
	else if (currentLessonId && entry.lessonId === currentLessonId) where = 'this lesson';
	else where = 'that lesson';
	return `${date} · ${where}`;
};

export const initialsOf = (name) =>
	String(name || '')
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((p) => p[0].toUpperCase())
		.join('') || '?';

/** Mention ids are deduped, self-mentions dropped, unknown people dropped. */
export const cleanMentions = (mentions, { authorId, staffUids }) => {
	const allowed = new Set(staffUids || []);
	return [...new Set(mentions || [])].filter((u) => u && u !== authorId && allowed.has(u));
};

/**
 * The doc a teacher's Save writes. Everything validated here is validated
 * again by the rules; this exists so a bad draft fails in the page with a
 * sentence rather than as a permission-denied.
 */
export const buildEntry = ({ text, tags, mentions, authorId, authorName, lessonId, staffUids }) => {
	const body = String(text || '').trim();
	if (!body) return { error: 'Write something before saving.' };
	if (body.length > MAX_PRIVATE_NOTE_LENGTH) {
		return { error: `That note is ${body.length} characters; the limit is ${MAX_PRIVATE_NOTE_LENGTH}.` };
	}
	if (!authorId) return { error: 'You are signed out.' };
	return {
		entry: {
			text: body,
			authorId,
			authorName: authorName || null,
			lessonId: lessonId || null,
			tags: knownTags([...new Set(tags || [])]),
			mentions: cleanMentions(mentions, { authorId, staffUids }),
		},
	};
};

/**
 * The "@" popover's query: the word being typed after an "@" that starts a word,
 * up to the caret, or null when the caret is not in one. "@Gr|" → "Gr".
 */
export const mentionQueryAt = (text, caret) => {
	const before = String(text || '').slice(0, caret);
	const m = before.match(/(^|\s)@([A-Za-z]*)$/);
	return m ? m[2] : null;
};

/** Replace the "@query" before the caret with "@First " and return the new text + caret. */
export const insertMention = (text, caret, firstName) => {
	const before = String(text || '').slice(0, caret);
	const after = String(text || '').slice(caret);
	// A replacement FUNCTION, never a "$1" string, so a name can never be read
	// as replacement syntax (CLAUDE.md → the `$` in .replace() trap).
	const pad = /^\s/.test(after) ? '' : ' ';
	const replaced = before.replace(/(^|\s)@([A-Za-z]*)$/, (_, lead) => `${lead}@${firstName}${pad}`);
	return { text: replaced + after, caret: replaced.length };
};
